import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import GovtLinksPanel from '../GovtLinksPanel';
import PropertyMessaging from '../PropertyMessaging';
import { MortgageCalculator, StampDutyCalculator } from '../PropertyCalculators';
import {
  Check, Lock, Shield, Clock, Loader2, FileCheck,
  Calendar as CalendarIcon, ChevronRight, Home, MessageCircle, LogIn
} from 'lucide-react';

import { useCurrencySettings } from '../../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PropertySidebar = ({
  property, id, user, access,
  sellerContact, contactRequestStatus,
  handlePayment, requestContact,
  setShowBookingModal,
  paymentLoading,
  buyerFee, tenantFee,
  setReferralType, setShowReferralModal,
}) => {
  const { symbol: currSymbol } = useCurrencySettings();
  return (
    <div className="space-y-6">
      {/* Service Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 p-4" data-testid="service-disclaimer">
        <div className="flex items-start gap-2">
          <Shield className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-amber-700 font-semibold mb-1">INTRODUCTION SERVICE ONLY</p>
            <p className="text-xs text-amber-600 leading-relaxed">
              Shopperden facilitates introductions only. Verify all details independently. Engage qualified professionals before any commitment. 
              <Link to="/terms" className="underline ml-1 font-medium">Full Terms</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Login Gate for non-authenticated users */}
      {!user && (
        <div className="luxury-card p-6 text-center" data-testid="login-gate-sidebar">
          <Lock className="h-8 w-8 text-[#E07A3A] mx-auto mb-3" />
          <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] font-semibold mb-2">Sign In to Continue</h3>
          <p className="text-sm text-slate-500 mb-4">Create a free account to arrange viewings, verify documents, and connect with sellers</p>
          <div className="flex flex-col gap-2">
            <Link to="/register">
              <Button className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-11 font-semibold text-sm tracking-wider" data-testid="sidebar-register-btn">
                <LogIn className="mr-2 h-4 w-4" />Create Free Account
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full border-slate-200 text-slate-600 hover:bg-slate-50 h-11 font-semibold text-sm tracking-wider" data-testid="sidebar-login-btn">
                Already have an account? Sign In
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Tenant Viewing (for Rent listings) */}
      {property.listing_type === 'rent' && (
        <div className="luxury-card p-6">
          <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-1 font-semibold">Arrange a Viewing</h3>
          <p className="text-xs text-slate-400 mb-4">Visit this rental property in person</p>
          {access.viewing_access ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600"><Check className="h-5 w-5" /><span className="font-semibold">Viewing Access Unlocked</span></div>
              <Button onClick={() => setShowBookingModal(true)} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold" data-testid="book-viewing-btn"><CalendarIcon className="mr-2 h-4 w-4" />Pick a Date & Time</Button>
              <p className="text-xs text-[#1A1A1A]/40 font-medium">We'll coordinate the visit with the landlord</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tenantFee === 0 ? (
                <div className="p-3 bg-green-50 border border-green-200 text-center">
                  <p className="text-green-700 text-sm font-bold">FREE for Tenants</p>
                  <p className="text-green-600/70 text-xs mt-0.5">Protected under the Tenant Fees Act 2019</p>
                </div>
              ) : (
                <div className="p-3 bg-[#F8FAFC] border border-slate-200">
                  <p className="text-sm font-semibold text-[#1A1A1A] mb-1">What you get for {currSymbol}{tenantFee}:</p>
                  <ul className="space-y-1">
                    <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />In-person property viewing arranged</li>
                    <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Direct landlord introduction</li>
                    <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Viewing coordination by our team</li>
                  </ul>
                </div>
              )}
              <Button onClick={() => handlePayment('tenant_viewing')} disabled={paymentLoading} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold" data-testid="tenant-viewing-btn">
                {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : tenantFee === 0 ? <><CalendarIcon className="mr-2 h-4 w-4" />Unlock Free Viewing Access</> : <><CalendarIcon className="mr-2 h-4 w-4" />Unlock Viewing — {currSymbol}{tenantFee}</>}
              </Button>
              {tenantFee > 0 && <p className="text-[10px] text-amber-600 mt-1">One-time fee. Non-refundable. <Link to="/terms" className="underline">Terms apply</Link></p>}
            </div>
          )}
        </div>
      )}

      {/* Buyer Verification (for Sale listings) */}
      {property.listing_type === 'sale' && (
        <div className="luxury-card p-6">
          <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-1 font-semibold">Unlock This Property</h3>
          <p className="text-xs text-slate-400 mb-4">Verify your identity to see full details & connect with the seller</p>
          {(() => {
            if (access.buyer_verification_access && contactRequestStatus === 'approved' && sellerContact) return (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 text-center">
                  <p className="text-green-700 text-sm font-bold flex items-center justify-center gap-1.5"><Check className="h-4 w-4" />All Unlocked — You're Connected!</p>
                </div>
                <div className="p-4 bg-[#FEF3EC]">
                  <p className="text-[#1A1A1A] font-semibold text-lg">{sellerContact.seller_name}</p>
                  <p className="text-[#1A1A1A]/70 font-medium">{sellerContact.seller_email}</p>
                  {sellerContact.is_agent_listing && sellerContact.agent_company && (
                    <p className="text-[#E07A3A] text-sm font-semibold mt-1">{sellerContact.agent_company}</p>
                  )}
                </div>
                <p className="text-xs text-green-600 font-medium flex items-center gap-1"><FileCheck className="h-3 w-3" />Documents matched — legal terms shared</p>
              </div>
            );
            if (access.buyer_verification_access && contactRequestStatus === 'pending') return (
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 text-center">
                  <p className="text-amber-700 text-sm font-bold flex items-center justify-center gap-1.5"><Clock className="h-4 w-4" />Verification In Progress</p>
                  <p className="text-amber-600/70 text-xs mt-1">Our admin team is verifying documents & checking legal compliance</p>
                </div>
              </div>
            );
            if (access.buyer_verification_access && contactRequestStatus === 'none') return (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 text-center">
                  <p className="text-green-700 text-sm font-bold flex items-center justify-center gap-1.5"><Check className="h-4 w-4" />Payment Complete</p>
                </div>
                <Button onClick={requestContact} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold" data-testid="request-verification-btn"><FileCheck className="mr-2 h-4 w-4" />Start Verification Now</Button>
                <p className="text-xs text-[#1A1A1A]/40 font-medium">Admin will verify your documents & prepare the legal contract</p>
              </div>
            );
            return (
            <div className="space-y-4">
              <div className="p-3 bg-[#F8FAFC] border border-slate-200">
                <p className="text-sm font-semibold text-[#1A1A1A] mb-1.5">What you get for {currSymbol}{buyerFee}:</p>
                <ul className="space-y-1.5">
                  <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Full address & property documents revealed</li>
                  <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Identity verification (yours & seller's)</li>
                  <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Legal sale contract prepared by admin</li>
                  <li className="text-xs text-slate-500 flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500 flex-shrink-0" />Direct seller connection + in-person viewing</li>
                </ul>
              </div>
              <Button onClick={() => handlePayment('buyer_verification')} disabled={paymentLoading} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-14 font-bold text-base tracking-wide" data-testid="buyer-verification-btn">
                {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileCheck className="mr-2 h-5 w-5" />Unlock Property — {currSymbol}{buyerFee}</>}
              </Button>
              <p className="text-[10px] text-amber-600 mt-1">One-time fee. Non-refundable. <Link to="/terms" className="underline">Terms apply</Link></p>
            </div>
            );
          })()}
        </div>
      )}

      {/* Viewing option for Sale listings (after buyer verification) */}
      {property.listing_type === 'sale' && access.buyer_verification_access && (
        <div className="luxury-card p-6">
          <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-1 font-semibold">Book a Viewing</h3>
          <p className="text-xs text-slate-400 mb-4">Visit this property in person</p>
          <div className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 text-center">
              <p className="text-green-700 text-sm font-bold flex items-center justify-center gap-1.5"><Check className="h-4 w-4" />Included in Your Package</p>
              <p className="text-green-600/70 text-xs mt-0.5">No extra charge — viewing is part of your verification fee</p>
            </div>
            <Button onClick={() => setShowBookingModal(true)} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold" data-testid="book-viewing-btn"><CalendarIcon className="mr-2 h-4 w-4" />Pick a Date & Time</Button>
          </div>
        </div>
      )}

      {/* Interactive Calculators */}
      {property.listing_type === 'sale' && (
        <div className="space-y-3" data-testid="property-calculators">
          <MortgageCalculator
            propertyPrice={property.price}
            country={property.country || 'UK'}
            currencySymbol={currSymbol}
          />
          <StampDutyCalculator
            propertyPrice={property.price}
            country={property.country || 'UK'}
            currencySymbol={currSymbol}
          />
        </div>
      )}

      {/* Listed By Info */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4 font-semibold">Listed By</h3>
        
        {/* Open Listing Banner */}
        {property.status === 'open' && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200" data-testid="open-listing-banner">
            <p className="text-xs font-semibold text-amber-700">This listing is open</p>
            <p className="text-xs text-amber-600 mt-1">The original agent's trial has ended. This property is available for another agent to represent, or you can proceed directly.</p>
            {user?.role === 'seller' && (
              <button onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/api/owner/claim-listing/${property.id}`);
                  toast.success(res.data.message);
                  window.location.reload();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to claim'); }
              }}
              className="mt-2 px-4 py-1.5 bg-[#E07A3A] text-white text-xs font-semibold tracking-wider hover:bg-[#C4652E] transition-colors"
              data-testid="owner-claim-btn">
                I'M THE OWNER — CLAIM THIS LISTING
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#FEF3EC] flex items-center justify-center">
            <span className="text-[#E07A3A] font-semibold text-lg">
              {property.listed_by_agent ? (property.agent_name?.charAt(0) || 'A') : (property.seller_name?.charAt(0) || 'S')}
            </span>
          </div>
          <div>
            {property.listed_by_agent ? (
              <>
                <p className="text-[#1A1A1A] font-semibold">{property.agent_name}</p>
                {property.agent_company && <p className="text-[#E07A3A] text-xs font-semibold">{property.agent_company}</p>}
                <p className="text-[#1A1A1A]/50 text-sm font-medium">{property.status === 'open' ? 'Agent Trial Expired' : 'Verified Agent'}</p>
              </>
            ) : (
              <>
                <p className="text-[#1A1A1A] font-semibold">Verified Seller</p>
                <p className="text-[#1A1A1A]/50 text-sm font-medium">Contact details protected by platform</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* In-App Messaging */}
      {user && ((access.buyer_verification_access && contactRequestStatus === 'approved') || (user.role === 'seller' && property.seller_id === user.id) || (user.role === 'agent' && property.agent_id === user.id)) && (
        <PropertyMessaging
          propertyId={id}
          currentUser={user}
          contactInfo={sellerContact}
        />
      )}

      {/* Professional Referrals */}
      {user && (access.buyer_verification_access || access.viewing_access) && (
        <div className="luxury-card p-6" data-testid="referral-section">
          <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-2 font-semibold">Need a Professional?</h3>
          <p className="text-sm text-[#1A1A1A]/50 mb-4">We can connect you with verified professionals</p>
          <div className="space-y-2">
            {[
              { type: 'solicitor', label: 'Find a Solicitor', desc: 'Legal & conveyancing' },
              { type: 'surveyor', label: 'Find a Surveyor', desc: 'Property inspections' },
              { type: 'mortgage', label: 'Find a Mortgage Adviser', desc: 'Finance & lending' },
            ].map(({ type, label, desc }) => (
              <button
                key={type}
                onClick={() => { setReferralType(type); setShowReferralModal(true); }}
                className="w-full flex items-center justify-between p-3 border border-slate-200 hover:border-[#E07A3A] hover:bg-[#FEF3EC] transition-colors text-left group"
                data-testid={`referral-${type}-btn`}
              >
                <div>
                  <span className="text-sm font-semibold text-[#1A1A1A] group-hover:text-[#E07A3A]">{label}</span>
                  <p className="text-xs text-slate-400">{desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-[#E07A3A]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Government Resources */}
      <div className="luxury-card p-6" data-testid="property-govt-links">
        <GovtLinksPanel country={property.country || 'UK'} />
      </div>
    </div>
  );
};

export default PropertySidebar;
